/**
 * Строка управления экрана брендов (§4.1).
 *
 * Делится надвое и живёт в шапке приложения двумя кусками. Слева, рядом с
 * логотипом, — то, что меняет сам вид таблицы: канал и формат отображения.
 * Справа, у аккаунта, — инструменты показа: «Колонки», «Фильтры», «Выгрузить».
 * Так в левом углу стоит смысл («что я смотрю»), а в правом — настройки
 * («как это показано»).
 *
 * Поиск сюда не входит: он переехал в шапку таблицы, в блок «Бренд».
 * Выпадающий список сортировки убран — сортировка живёт в заголовках колонок,
 * а четыре пресета в шапке дублировали её и занимали место.
 *
 * Меню колонок принадлежит таблице — строка даёт ему кнопку и якорь через
 * `renderColumnsMenu`, а сама его не рисует.
 */
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { ReactNode } from 'react'
import {
  Button, ChannelSwitch, IconArrowDown, IconColumns, IconFilter, IconPlus, IconSparkle,
  Popover, Select,
} from '../../../components/index.js'
import { getPrefs, setPref, subscribePrefs, type DensityPref } from '../../../app/prefs.js'
import { texts } from '../../../texts.js'
import { api } from '../../../lib/api.js'
import type { SelectOption } from '../../../components/index.js'
import type { BrandFiltersState } from './useBrandFilters.js'
import { filterTexts } from './texts.filters.js'
import './FilterBar.css'

export interface FilterBarProps {
  state: BrandFiltersState
  /** Видна ли панель фильтров над таблицей и кто ею управляет. */
  filtersOpen: boolean
  onToggleFilters: () => void
  /** Набор колонок сужен вручную (ссылка с `cols=`): это надо видеть. */
  columnsCustom?: boolean
  onResetColumns?: () => void
  /**
   * Меню выбора колонок рисует волна таблицы: она знает набор колонок и их
   * ширины. Строка управления отдаёт ей кнопку-якорь и состояние открытости.
   */
  renderColumnsMenu?: (args: { anchor: HTMLElement | null; open: boolean; onClose: () => void }) => ReactNode
  /** Заведение бренда: кнопку даёт строка управления, диалог рисует экран. */
  canCreateBrand?: boolean
  onCreateBrand?: () => void
  /** Копилот: вопрос по таблице. */
}

/** Правая группа уезжает к аккаунту; пока слота нет — рисуется на месте. */
function RightSlot({ children }: { children: ReactNode }) {
  const [host, setHost] = useState<HTMLElement | null>(null)
  useEffect(() => { setHost(document.getElementById('bm-toolbar-right')) }, [])
  return host ? createPortal(children, host) : <>{children}</>
}

const DENSITY_OPTIONS: SelectOption[] = [
  { value: 'normal', label: texts.topbar.density.normal },
  { value: 'compact', label: texts.topbar.density.compact },
]

export function FilterBar({
  state, renderColumnsMenu, filtersOpen, onToggleFilters, columnsCustom = false, onResetColumns,
  canCreateBrand = false, onCreateBrand,
}: FilterBarProps) {
  const [columnsOpen, setColumnsOpen] = useState(false)
  const [exportOpen, setExportOpen] = useState(false)
  const [densityOpen, setDensityOpen] = useState(false)
  /* Плотность живёт вне React (localStorage), поэтому строка управления на неё
     подписывается — иначе контрол не перерисуется после переключения. */
  const [density, setDensity] = useState<DensityPref>(() => getPrefs().density)
  useEffect(() => subscribePrefs(() => setDensity(getPrefs().density)), [])
  const columnsRef = useRef<HTMLButtonElement | null>(null)
  const filtersRef = useRef<HTMLButtonElement | null>(null)
  const [exportAnchor, setExportAnchor] = useState<HTMLButtonElement | null>(null)

  return (
    <div className="filter-bar" role="group" aria-label={filterTexts.bar.label}>
      {/* Канал — капсом: это две разные вселенные данных, и подпись должна
          читаться как режим, а не как ещё одна кнопка. */}
      <ChannelSwitch
        value={state.channel}
        onChange={state.setChannel}
        caps
      />

      {/* Формат отображения — выпадающий список, а не вторая такая же
          переключалка: рядом стоящие одинаковые сегменты читались как один
          контрол из четырёх кнопок. */}
      <Select
        className="filter-bar__density"
        value={density}
        options={DENSITY_OPTIONS}
        open={densityOpen}
        onOpenChange={setDensityOpen}
        label={filterTexts.bar.densityLabel}
        allowEmpty={false}
        clearLabel=""
        onChange={(value) => { if (value) setPref('density', value as DensityPref) }}
      />

      <RightSlot>
        <>
          {/* Завести бренд. Диалог был написан, но кнопки к нему не было
              нигде — завести бренд было попросту нечем. */}
          {canCreateBrand && onCreateBrand ? (
            <Button
              variant="secondary"
              size="s"
              iconBefore={<IconPlus size={16} />}
              aria-label={filterTexts.bar.addBrand}
              onClick={onCreateBrand}
            >
              <span className="filter-bar__label">{filterTexts.bar.addBrand}</span>
            </Button>
          ) : null}

          <Button
            ref={columnsRef}
            className="filter-bar__columns"
            variant="ghost"
            size="s"
            iconBefore={<IconColumns size={16} />}
            aria-label={filterTexts.bar.columns}
            aria-expanded={columnsOpen}
            aria-haspopup="menu"
            onClick={() => setColumnsOpen((open) => !open)}
          >
            <span className="filter-bar__label">{filterTexts.bar.columns}</span>
          </Button>
          {renderColumnsMenu?.({
            anchor: columnsRef.current,
            open: columnsOpen,
            onClose: () => setColumnsOpen(false),
          })}

          {/* Набор колонок приходит и ссылкой: по чужой ссылке таблица может
              открыться с тремя колонками, и это выглядит как пропавшие данные.
              Говорим об этом прямо и даём вернуть всё одним нажатием. */}
          {columnsCustom && onResetColumns ? (
            <button type="button" className="filter-bar__reset-cols t-meta" onClick={onResetColumns}>
              {filterTexts.bar.columnsCustom}
            </button>
          ) : null}

          {/* Кнопка не открывает своё окно: она показывает и прячет панель
              фильтров над шапкой таблицы — фильтры живут только там. */}
          <Button
            ref={filtersRef}
            variant={state.activeCount > 0 ? 'secondary' : 'ghost'}
            size="s"
            iconBefore={<IconFilter size={16} />}
            aria-label={filterTexts.bar.filters(state.activeCount)}
            aria-expanded={filtersOpen}
            aria-controls="bm-filters"
            onClick={onToggleFilters}
          >
            <span className="filter-bar__label">{filterTexts.bar.filters(state.activeCount)}</span>
          </Button>

          {/* Выгрузка текущего среза. XLSX — основной формат: его открывают и
              Excel, и Google Таблицы; CSV оставлен для импорта в другие системы. */}
          <Button
            ref={setExportAnchor}
            variant="ghost"
            size="s"
            iconBefore={<IconArrowDown size={16} />}
            aria-label={filterTexts.bar.export}
            aria-expanded={exportOpen}
            aria-haspopup="menu"
            onClick={() => setExportOpen((open) => !open)}
          >
            <span className="filter-bar__label">{filterTexts.bar.export}</span>
          </Button>
          <Popover
            open={exportOpen}
            anchor={exportAnchor}
            onClose={() => setExportOpen(false)}
            role="menu"
            placement="bottom-end"
            minWidth={260}
          >
            <a
              className="filter-bar__export"
              href={api.exportXlsxUrl()}
              download
              role="menuitem"
              onClick={() => setExportOpen(false)}
            >
              <span className="t-cell">{filterTexts.bar.exportXlsx}</span>
              <span className="t-meta">{filterTexts.bar.exportXlsxHint}</span>
            </a>
            <a
              className="filter-bar__export"
              href={api.exportCsvUrl()}
              download
              role="menuitem"
              onClick={() => setExportOpen(false)}
            >
              <span className="t-cell">{filterTexts.bar.exportCsv}</span>
              <span className="t-meta">{filterTexts.bar.exportCsvHint}</span>
            </a>
          </Popover>
        </>
      </RightSlot>
    </div>
  )
}
